import { useMemo, useState, type UIEvent } from "react";
import {
  Button,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Spinner,
  Tab,
  Tabs,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { BellOff, CheckCheck } from "lucide-react";
import type { NotificationItem } from "../services/notifications";
import { NotificationCard } from "./NotificationCard";

type NotificationDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: (isOpen: boolean) => void;
  notifications: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  isMarkingAsRead: boolean;
  onMarkAsRead: (notificationId: number) => void;
  onMarkAllAsRead: () => void;
};

type FilterTab = "all" | "unread";

export function NotificationDrawer({
  isOpen,
  onOpenChange,
  notifications,
  unreadCount,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isMarkingAsRead,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationDrawerProps) {
  const [filter, setFilter] = useState<FilterTab>("unread");

  const handleNotificationsScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage || isLoading) {
      return;
    }

    const target = event.currentTarget;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;
    const shouldLoadMore = distanceFromBottom < 120;

    if (shouldLoadMore) {
      onLoadMore();
    }
  };

  const visibleNotifications = useMemo(
    () =>
      filter === "unread"
        ? notifications.filter((notification) => !notification.isRead)
        : notifications,
    [filter, notifications],
  );

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="right"
      size="sm"
      classNames={{
        base: "rounded-l-large",
        wrapper: "!p-0",
        closeButton:
          "top-[calc(0.5rem_+_var(--safe-area-top,0px))] right-[calc(0.5rem_+_var(--safe-area-right,0px))]",
      }}
    >
      <DrawerContent>
        <DrawerHeader className="border-divider flex flex-col gap-3 border-b px-5 pt-[calc(1rem_+_var(--safe-area-top,0px))] pb-3">
          <div className="flex items-baseline gap-2 pr-10">
            <h2 className="text-base font-semibold">
              <Trans>Notifications</Trans>
            </h2>
            {unreadCount > 0 && (
              <Chip size="sm" color="primary" variant="flat">
                {unreadCount}
              </Chip>
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <Tabs
              size="sm"
              variant="solid"
              color="primary"
              selectedKey={filter}
              onSelectionChange={(key) => setFilter(key as FilterTab)}
            >
              <Tab
                key="unread"
                title={
                  <span className="flex items-center gap-1.5">
                    <Trans>Unread</Trans>
                    {unreadCount > 0 && (
                      <span className="bg-default-200 text-default-700 rounded-full px-1.5 text-[10px] font-semibold">
                        {unreadCount}
                      </span>
                    )}
                  </span>
                }
              />
              <Tab key="all" title={t`All`} />
            </Tabs>

            <Button
              size="sm"
              variant="light"
              color="primary"
              startContent={<CheckCheck size={14} />}
              isDisabled={isMarkingAsRead || unreadCount === 0}
              onPress={onMarkAllAsRead}
            >
              <Trans>Označi vse kot prebrano</Trans>
            </Button>
          </div>
        </DrawerHeader>

        <DrawerBody
          className="min-h-0 flex-1 overflow-y-auto px-3 pt-3 pb-[calc(0.75rem_+_var(--safe-area-bottom,0px))]"
          onScroll={handleNotificationsScroll}
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size="sm" />
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="text-default-500 flex h-full flex-col items-center justify-center gap-2 text-center">
              <div className="bg-default-100 mb-1 flex h-12 w-12 items-center justify-center rounded-full">
                <BellOff size={20} className="text-default-400" />
              </div>
              <p className="text-sm font-medium">
                {filter === "unread" ? (
                  <Trans>Vse je prebrano</Trans>
                ) : (
                  <Trans>Ni novih obvestil</Trans>
                )}
              </p>
              <p className="text-default-400 max-w-[220px] text-xs">
                {filter === "unread" ? (
                  <Trans>Nova neprebrana obvestila bodo prikazana tukaj.</Trans>
                ) : (
                  <Trans>
                    Tu boste videli posodobitve o rezervacijah, plačilih in
                    drugo.
                  </Trans>
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-2 pr-1">
              {visibleNotifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  isMarkingAsRead={isMarkingAsRead}
                  onMarkAsRead={onMarkAsRead}
                  closeDrawer={() => onOpenChange(false)}
                />
              ))}

              {isFetchingNextPage && (
                <div className="flex justify-center py-3">
                  <Spinner size="sm" />
                </div>
              )}
            </div>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
